/**
 * Step Change Analysis Utilities
 *
 * Shared utilities for analyzing step changes in workflows.
 * Used by both ChangeList (inline badges) and ChangeDetailModal (detailed view).
 */

export interface StepInfo {
  orchestration_step_id: number;
  lineage_id?: string;
  step_type: string;
  label?: string;
  description?: string;
  agent_id?: number;
}

export interface StepChangesSummary {
  added: StepInfo[];
  deleted: StepInfo[];
  modified: Array<{ before: StepInfo; after: StepInfo; changedFields: string[] }>;
}

/**
 * Get a display name for a step
 */
export function getStepDisplayName(step: Pick<StepInfo, 'label' | 'step_type'>): string {
  if (step.label) {
    return step.label;
  }
  // Format step_type nicely
  const typeMap: Record<string, string> = {
    'user_prompt': 'User Prompt',
    'auto_prompt': 'Auto Prompt',
    'run_agent': 'Run Agent',
    'output_message': 'Output Message',
    'condition': 'Condition',
    'queue_read': 'Queue Read',
    'queue_write': 'Queue Write',
  };
  return typeMap[step.step_type] || step.step_type;
}

/**
 * Compare two step objects and return changed field names
 */
export function getChangedStepFields(before: StepInfo, after: StepInfo): string[] {
  const changed: string[] = [];
  const beforeObj = before as unknown as Record<string, unknown>;
  const afterObj = after as unknown as Record<string, unknown>;
  const allKeys = Array.from(new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]));

  for (const key of allKeys) {
    if (key === 'orchestration_step_id' || key === 'lineage_id') continue; // Skip identity fields
    const oldVal = beforeObj[key];
    const newVal = afterObj[key];
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changed.push(key);
    }
  }
  return changed;
}

/**
 * Analyze step changes between previous and new state
 */
export function analyzeStepChanges(
  previousState: Record<string, unknown> | null,
  newState: Record<string, unknown> | null
): StepChangesSummary | null {
  const prevSteps = (previousState?.steps || []) as StepInfo[];
  const newSteps = (newState?.steps || []) as StepInfo[];

  if (prevSteps.length === 0 && newSteps.length === 0) {
    return null;
  }

  // Create maps by lineage_id (primary) or orchestration_step_id (fallback)
  const prevStepMap = new Map<string, StepInfo>();
  const newStepMap = new Map<string, StepInfo>();

  const stepKey = (step: StepInfo): string =>
    step.lineage_id || `id:${step.orchestration_step_id}`;

  prevSteps.forEach(step => {
    const key = stepKey(step);
    if (key) prevStepMap.set(key, step);
  });

  newSteps.forEach(step => {
    const key = stepKey(step);
    if (key) newStepMap.set(key, step);
  });

  const added: StepInfo[] = [];
  const deleted: StepInfo[] = [];
  const modified: Array<{ before: StepInfo; after: StepInfo; changedFields: string[] }> = [];

  // Find deleted steps (in prev but not in new)
  prevStepMap.forEach((step, key) => {
    if (!newStepMap.has(key)) {
      deleted.push(step);
    }
  });

  // Find added steps (in new but not in prev)
  newStepMap.forEach((step, key) => {
    if (!prevStepMap.has(key)) {
      added.push(step);
    }
  });

  // Find modified steps (in both but different)
  prevStepMap.forEach((prevStep, key) => {
    const newStep = newStepMap.get(key);
    if (newStep) {
      const changedFields = getChangedStepFields(prevStep, newStep);
      if (changedFields.length > 0) {
        modified.push({ before: prevStep, after: newStep, changedFields });
      }
    }
  });

  // Only return if there are actual changes
  if (added.length === 0 && deleted.length === 0 && modified.length === 0) {
    return null;
  }

  return { added, deleted, modified };
}
